import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-none border-none px-1 py-px text-xs w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        success:
          'border-transparent bg-wb-grass/20 text-wb-grass uppercase [a&]:hover:bg-wb-grass/30',
        orange:
          'border-transparent bg-wb-orange/20 text-wb-brown uppercase [a&]:hover:bg-wb-orange/30',
        purple:
          'border-transparent bg-wb-purple/20 text-wb-purple uppercase [a&]:hover:bg-wb-purple/30',
        sienna:
          'border-transparent bg-wb-sienna/20 text-wb-sienna uppercase [a&]:hover:bg-wb-sienna/30',
        deepRed:
          'border-transparent bg-wb-deep-red/20 text-wb-deep-red uppercase [a&]:hover:bg-wb-deep-red/30',
        yellow:
          'border-transparent bg-wb-yellow/20 text-wb-brown uppercase [a&]:hover:bg-wb-yellow/30',
        green:
          'border-transparent bg-wb-green/20 text-wb-green uppercase [a&]:hover:bg-wb-green/30',
        blue: 'border-transparent bg-wb-blue/20 text-wb-blue uppercase [a&]:hover:bg-wb-blue/30',
        gray: 'border-transparent bg-wb-gray/20 text-wb-gray uppercase [a&]:hover:bg-wb-gray/30',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Badge({
  className,
  variant = 'default',
  render,
  ...props
}: useRender.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ variant }), className),
      },
      props
    ),
    render,
    state: {
      slot: 'badge',
      variant,
    },
  });
}

export { Badge, badgeVariants };
